import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { X, ShoppingCart, Plus, Minus, CheckCircle } from 'lucide-react';
import { formatPrice } from '../lib/utils';
import { useCart } from '../context/CartContext';

interface Product {
  id: string;
  name: string;
  price: number;
  discountPrice?: number;
  image: string;
  stock: number;
}

interface AddToCartModalProps {
  isOpen: boolean;
  onClose: () => void;
  product: Product;
  initialQuantity?: number;
}

const AddToCartModal: React.FC<AddToCartModalProps> = ({
  isOpen,
  onClose,
  product,
  initialQuantity = 1
}) => {
  const { addItem, updateQuantity, cart } = useCart();
  const [quantity, setQuantity] = useState(initialQuantity);

  // Find current quantity in cart
  const cartItem = cart.items.find(item => item.product.id === product.id);
  const currentCartQuantity = cartItem?.quantity || 0;

  const handleQuantityChange = (newQuantity: number) => {
    if (newQuantity >= 1 && newQuantity <= product.stock) {
      setQuantity(newQuantity);
      if (cartItem) {
        updateQuantity(product.id, newQuantity);
      }
    }
  };

  const handleUpdateCart = () => {
    if (cartItem) {
      updateQuantity(product.id, quantity);
    } else {
      addItem(product, quantity);
    }
  };

  const currentPrice = product.discountPrice || product.price;
  const totalPrice = currentPrice * quantity;

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-black bg-opacity-50"
            onClick={onClose}
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            transition={{ duration: 0.3 }}
            className="relative bg-white rounded-lg shadow-xl max-w-md w-full mx-4 overflow-hidden"
          >
            {/* Header */}
            <div className="bg-green-50 px-6 py-4 border-b border-green-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                    <CheckCircle className="h-5 w-5 text-green-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-green-800">
                    Added to Cart!
                  </h3>
                </div>
                <button
                  onClick={onClose}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="p-6">
              {/* Product Info */}
              <div className="flex items-start space-x-4 mb-6">
                <div className="w-20 h-20 bg-gray-100 rounded-lg overflow-hidden flex-shrink-0">
                  <img
                    src={product.image}
                    alt={product.name}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className="font-medium text-gray-900 line-clamp-2 mb-2">
                    {product.name}
                  </h4>
                  <div className="flex items-center space-x-2">
                    <span className="font-bold text-lg text-gray-900">
                      {formatPrice(currentPrice)}
                    </span>
                    {product.discountPrice && (
                      <span className="text-sm text-gray-500 line-through">
                        {formatPrice(product.price)}
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Quantity Selector */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Quantity
                </label>
                <div className="flex items-center justify-between">
                  <div className="flex items-center border border-gray-300 rounded-md overflow-hidden">
                    <button
                      onClick={() => handleQuantityChange(quantity - 1)}
                      disabled={quantity <= 1}
                      className="px-3 py-2 bg-gray-50 text-gray-600 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      <Minus className="h-4 w-4" />
                    </button>
                    <input
                      type="number"
                      min="1"
                      max={product.stock}
                      value={quantity}
                      onChange={(e) => handleQuantityChange(parseInt(e.target.value) || 1)}
                      className="w-16 text-center border-x border-gray-300 py-2 focus:outline-none"
                    />
                    <button
                      onClick={() => handleQuantityChange(quantity + 1)}
                      disabled={quantity >= product.stock}
                      className="px-3 py-2 bg-gray-50 text-gray-600 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      <Plus className="h-4 w-4" />
                    </button>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-gray-600">Total</p>
                    <p className="font-bold text-lg text-gray-900">
                      {formatPrice(totalPrice)}
                    </p>
                  </div>
                </div>
              </div>

              {/* Current Cart Info */}
              {currentCartQuantity > 0 && (
                <div className="mb-6 p-3 bg-blue-50 rounded-lg border border-blue-200">
                  <p className="text-sm text-blue-800">
                    You currently have <span className="font-medium">{currentCartQuantity}</span> of this item in your cart.
                  </p>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  onClick={onClose}
                  className="flex-1 btn btn-outline py-3 px-4 text-center"
                >
                  Continue Shopping
                </button>
                <Link
                  to="/cart"
                  onClick={onClose}
                  className="flex-1 btn btn-primary py-3 px-4 text-center flex items-center justify-center space-x-2"
                >
                  <ShoppingCart className="h-4 w-4" />
                  <span>Go to Cart</span>
                </Link>
              </div>

              {/* Update Cart Button (if quantity changed) */}
              {cartItem && quantity !== currentCartQuantity && (
                <button
                  onClick={handleUpdateCart}
                  className="w-full mt-3 btn btn-secondary py-2 px-4"
                >
                  Update Cart Quantity
                </button>
              )}
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default AddToCartModal;